import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChartNoAxesColumn, CircleCheck, ClipboardCheck, Copy, TriangleAlert, X } from "lucide-react";
import { useGroomingMetrics } from "@/hooks/queries/use-grooming";
import type { GroomingMetrics } from "@/lib/api/grooming";
import {
  NO_CURRENT_SPRINT,
  PROGRESS_AUDIENCE,
  PROGRESS_FOOTNOTE,
  PROGRESS_WINDOWS,
  SIZING_TOO_EARLY,
  SPRINT_UNAVAILABLE_WORDS,
  SPRINT_UNREADABLE,
  countOf,
  itemsWord,
  noSessionsLine,
  preparationLine,
  sessionDay,
  sessionsNewestFirst,
  sessionFill,
  sessionsWord,
  sizedDotTitle,
  sizingLine,
  sprintCaption,
  sprintLine,
  withBacklogSprint,
  windowDates,
  type ProgressItemsKind,
  type ProgressWindow,
} from "@/lib/grooming/grooming-progress";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * "How grooming is going" (epic PI-SPEC-SW-E293, feature PI-SPEC-SW-1504,
 * UX-PM-01, frames E1 and E4): the Backlog's 380 px progress panel for people
 * with `read-grooming-progress` (Project Managers, Admins, Product Owners and
 * Viewers). Three lines as counts in words: Sizing (the team's size inside
 * Claude's range), Preparation (agenda items with a brief before the meeting)
 * and This sprint (items that entered it ungroomed), over the last 30, 60 or
 * 90 days, with the advisory footnote.
 *
 * Never a percentage, and Claude's size only for items the team has already
 * sized. "See the … ›" opens the items behind a line through `onOpenItems`
 * (frame E2, feature PI-SPEC-SW-1505); Copy summary puts the plain text from
 * `summaryFor` on the clipboard and shows what was copied in place of the
 * figures (frame E3), with no pop-up.
 */

/** The line whose items `onOpenItems` opens. */
export type { ProgressItemsKind };

/** Beside the list (List view) or over the board (Stages), as the reading pane. */
export type ProgressPanelVariant = "side" | "over";

const PANEL_CLASSES: Record<ProgressPanelVariant, string> = {
  side: "relative w-[380px] flex-none border-l border-[#1F1F23] bg-[#0B0B0D]",
  over: "absolute inset-y-0 right-0 z-10 w-[380px] border-l border-[#1F1F23] bg-[#0B0B0D] shadow-[-24px_0_60px_rgba(0,0,0,0.5)]",
};

/** The design's secondary button: 30 px tall, radius 8, #3F3F46 border on #1F1F23. */
export const SECONDARY_BUTTON =
  "inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-[#3F3F46] bg-[#1F1F23] px-2.5 text-sm font-medium text-foreground transition-colors hover:bg-[#27272A] disabled:cursor-default disabled:opacity-45";

/** The Backlog header's "How it's going" button (frame E1); pressed while the panel shows. */
export function ProgressPanelButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={SECONDARY_BUTTON}
      aria-pressed={open}
      data-testid="progress-panel-button"
      onClick={onClick}
    >
      <ChartNoAxesColumn className="h-3.5 w-3.5" aria-hidden="true" />
      How it&apos;s going
    </button>
  );
}

/** The 30 / 60 / 90 days switch (frames E1 and E2). */
export function WindowSwitch({ value, onChange }: { value: ProgressWindow; onChange: (days: ProgressWindow) => void }) {
  return (
    <div
      role="group"
      aria-label="Time span"
      className="inline-flex gap-0.5 rounded-[9px] border border-[#1F1F23] bg-[#111113] p-[3px]"
    >
      {PROGRESS_WINDOWS.map((days) => (
        <button
          key={days}
          type="button"
          aria-pressed={days === value}
          onClick={() => {
            onChange(days);
          }}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            days === value ? "bg-[#1F1F23] text-[#FAFAFA]" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {days} days
        </button>
      ))}
    </div>
  );
}

/** One of the three lines: an eyebrow over its content. */
function Block({ eyebrow, testId, children }: { eyebrow: string; testId: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-[7px] border-b border-[#18181B] px-4 py-3.5" data-testid={testId} aria-label={eyebrow}>
      <h3 className={TOOLBOX_TEXT.eyebrow}>{eyebrow}</h3>
      {children}
    </section>
  );
}

/** The big count: "7 of 9" (Golos Text 26 px bold). */
function BigCount({ children }: { children: ReactNode }) {
  return (
    <p className="font-display text-[26px] font-bold leading-none tracking-[-0.02em] text-foreground">{children}</p>
  );
}

/** "See the 9 items ›": opens the items behind the line (the next feature fills it in). */
function SeeLink({ children, onClick, testId }: { children: ReactNode; onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      className="flex-none text-[13px] text-[#6CC4FF] hover:underline"
      data-testid={testId}
      onClick={onClick}
    >
      {children} ›
    </button>
  );
}

function SizingBlock({ metrics, onOpen }: { metrics: GroomingMetrics; onOpen: () => void }) {
  const agreement = metrics.estimateAgreement;
  const line = sizingLine(agreement);
  const emptySlots = Array.from({ length: line.slots }, (_, index) => (
    <span
      key={`slot-${String(index)}`}
      className="h-3 w-3 rounded-full border border-dashed border-[#52525B]"
      data-testid="sizing-slot"
      aria-hidden="true"
    />
  ));
  return (
    <Block eyebrow="Sizing" testId="progress-sizing">
      {line.kind === "none" ? (
        <p className="text-sm text-foreground">No sized items yet</p>
      ) : (
        <>
          <BigCount>{countOf(line.inside, line.count)}</BigCount>
          <p className="text-[13px] text-[#D4D4D8]">times the team&apos;s size landed inside Claude&apos;s range</p>
        </>
      )}
      <div
        className="flex flex-wrap gap-1.5 py-0.5"
        role="img"
        aria-label={
          line.kind === "none" ? "No sized items yet" : `${countOf(line.inside, line.count)} inside Claude's range`
        }
        data-testid="sizing-dots"
      >
        {agreement.items.map((sized) => (
          <span
            key={sized.itemId}
            title={sizedDotTitle(sized)}
            data-testid="sizing-dot"
            data-landed={sized.landed}
            className={cn(
              "h-3 w-3 rounded-full",
              sized.landed === "inside" ? "bg-[#22C55E]" : "border-2 border-[#A1A1AA]"
            )}
          />
        ))}
        {emptySlots}
      </div>
      <div className="flex items-start gap-3">
        {line.kind === "judged" ? (
          <p className="flex min-w-0 flex-1 items-center gap-3 text-xs text-muted-foreground" data-testid="sizing-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-[9px] w-[9px] rounded-full bg-[#22C55E]" aria-hidden="true" />
              inside the range
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-[9px] w-[9px] rounded-full border-2 border-[#A1A1AA]" aria-hidden="true" />
              outside
            </span>
          </p>
        ) : (
          <p className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid="sizing-too-early">
            {SIZING_TOO_EARLY}
          </p>
        )}
        {line.kind !== "none" && (
          <SeeLink testId="see-sizing" onClick={onOpen}>
            See the {itemsWord(line.count)}
          </SeeLink>
        )}
      </div>
    </Block>
  );
}

function PreparationBlock({
  metrics,
  sinceDays,
  onOpen,
}: {
  metrics: GroomingMetrics;
  sinceDays: number;
  onOpen: () => void;
}) {
  const coverage = metrics.prepCoverage;
  const sessions = coverage.batches.length;
  return (
    <Block eyebrow="Preparation" testId="progress-preparation">
      {sessions === 0 ? (
        <p className={TOOLBOX_TEXT.content}>{noSessionsLine(sinceDays)}</p>
      ) : (
        <>
          <BigCount>{countOf(coverage.briefedOrCurrent, coverage.items)}</BigCount>
          <p className="text-[13px] text-[#D4D4D8]">{preparationLine(sessions)}</p>
          <div className="grid grid-cols-[48px_1fr_44px] items-center gap-x-2.5 gap-y-1.5 py-0.5">
            {sessionsNewestFirst(coverage.batches).map((session) => (
              <div key={session.batchId} className="contents" data-testid="session-row">
                <span className="text-xs text-muted-foreground">{sessionDay(session)}</span>
                <span className="h-1.5 overflow-hidden rounded-[3px] bg-[#27272A]" aria-hidden="true">
                  {/* The fill's width is the one runtime value. */}
                  <span
                    className="block h-full rounded-[3px] bg-[#A1A1AA]"
                    style={{ width: `${String(sessionFill(session) * 100)}%` }}
                  />
                </span>
                <span className="text-xs text-foreground">{countOf(session.briefedOrCurrent, session.items)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <SeeLink testId="see-preparation" onClick={onOpen}>
              See the {sessionsWord(sessions)}
            </SeeLink>
          </div>
        </>
      )}
    </Block>
  );
}

function SprintBlock({ metrics, onOpen }: { metrics: GroomingMetrics; onOpen: () => void }) {
  const line = sprintLine(metrics);
  return (
    <Block eyebrow="This sprint" testId="progress-sprint">
      {line.kind === "entered" ? (
        <>
          <p className="flex items-start gap-2 text-sm text-foreground">
            <TriangleAlert className="mt-0.5 h-4 w-4 flex-none text-[#FBBF24]" aria-hidden="true" />
            <span>
              <strong className="font-semibold">{itemsWord(line.count)}</strong> entered {line.sprint} without being
              groomed
            </span>
          </p>
          <div className="flex items-center gap-3 pl-6">
            <span className="min-w-0 flex-1 text-xs text-muted-foreground">{sprintCaption(line)}</span>
            <SeeLink testId="see-sprint" onClick={onOpen}>
              See the {itemsWord(line.count)}
            </SeeLink>
          </div>
        </>
      ) : line.kind === "none-entered" ? (
        <p className="flex items-start gap-2 text-sm text-foreground">
          <CircleCheck className="mt-0.5 h-4 w-4 flex-none text-[#4ADE80]" aria-hidden="true" />
          <span>Nothing entered {line.sprint} ungroomed</span>
        </p>
      ) : (
        <p className={TOOLBOX_TEXT.content}>
          {line.kind === "no-sprint"
            ? NO_CURRENT_SPRINT
            : line.reason
              ? SPRINT_UNAVAILABLE_WORDS[line.reason]
              : SPRINT_UNREADABLE}
        </p>
      )}
    </Block>
  );
}

// ---------------------------------------------------------------------------
// Copy summary (frame E3)
// ---------------------------------------------------------------------------

/** How long the button reads "Copied" after a successful copy. */
export const COPIED_FOR_MS = 3000;

/** The last Copy summary: on the clipboard, or shown to copy by hand when the clipboard refused it. */
export interface CopyOutcome {
  status: "copied" | "failed";
  text: string;
}

/**
 * Copy summary's state: writes with `navigator.clipboard.writeText`, keeps
 * what was copied (or what could not be) for the panel to show, and reads
 * "Copied" on the button for {@link COPIED_FOR_MS}. Never a toast.
 */
export function useCopySummary() {
  const [outcome, setOutcome] = useState<CopyOutcome | null>(null);
  const [justCopied, setJustCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopTimer = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  useEffect(() => stopTimer, [stopTimer]);

  const copy = useCallback(
    async (text: string) => {
      stopTimer();
      setJustCopied(false);
      try {
        const clipboard = navigator.clipboard as Clipboard | undefined;
        if (!clipboard) throw new Error("No clipboard");
        await clipboard.writeText(text);
      } catch {
        setOutcome({ status: "failed", text });
        return;
      }
      setOutcome({ status: "copied", text });
      setJustCopied(true);
      timer.current = setTimeout(() => {
        timer.current = null;
        setJustCopied(false);
      }, COPIED_FOR_MS);
    },
    [stopTimer]
  );

  const dismiss = useCallback(() => {
    stopTimer();
    setJustCopied(false);
    setOutcome(null);
  }, [stopTimer]);

  return { outcome, justCopied, copy, dismiss };
}

/** The Copy summary button: check and "Copied" for 3 seconds after a copy. */
export function CopySummaryButton({
  justCopied,
  disabled,
  onClick,
}: {
  justCopied: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  // Never shrinks: beside the "Seen by" line in the panel footer it wrapped inside its 30 px height.
  return (
    <button
      type="button"
      className={cn(SECONDARY_BUTTON, "shrink-0 whitespace-nowrap")}
      disabled={disabled}
      data-testid="progress-copy"
      onClick={onClick}
    >
      {justCopied ? (
        <Check className="h-3.5 w-3.5 text-[#4ADE80]" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {justCopied ? "Copied" : "Copy summary"}
    </button>
  );
}

/**
 * What Copy summary put on the clipboard (frame E3), or, when the clipboard
 * refused it, the same text to copy by hand. Replaces the figures until
 * "Back to the figures".
 */
export function CopiedSummary({ outcome, onBack }: { outcome: CopyOutcome; onBack: () => void }) {
  const copied = outcome.status === "copied";
  return (
    <div className="flex flex-col gap-2.5 px-4 py-3.5" data-testid="copied-summary" data-status={outcome.status}>
      <p
        className="flex items-start gap-2 rounded-lg border border-[rgba(35,164,255,0.28)] bg-[rgba(35,164,255,0.10)] px-3 py-2.5 text-[13px] text-[#BFE3FF]"
        role="status"
      >
        <ClipboardCheck className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
        {copied ? (
          <span>
            <strong className="font-semibold">Summary copied.</strong> Paste it into an e-mail or a Teams chat.
          </span>
        ) : (
          <span>
            <strong className="font-semibold">The clipboard could not be reached.</strong> Copy it from here: select
            the text below and copy it.
          </span>
        )}
      </p>
      <h3 className={TOOLBOX_TEXT.eyebrow}>{copied ? "What you copied" : "The summary"}</h3>
      <pre
        className="select-text whitespace-pre-wrap rounded-lg border border-[#27272A] bg-[#111113] px-3.5 py-3 font-sans text-[12.5px] leading-[1.55] text-[#E4E4E7]"
        data-testid="copied-summary-text"
      >
        {outcome.text}
      </pre>
      <p className="text-xs text-muted-foreground">
        Plain text, so it reads the same anywhere. Item lists are left out; whoever wants them opens the panel.
      </p>
      <button type="button" className="self-start text-[13px] text-[#6CC4FF] hover:underline" onClick={onBack}>
        Back to the figures
      </button>
    </div>
  );
}

export interface GroomingProgressPanelProps {
  project: string;
  /** The look-back window; the page keeps it so it survives the reading pane swapping in. */
  sinceDays: ProgressWindow;
  onSinceDaysChange: (days: ProgressWindow) => void;
  variant: ProgressPanelVariant;
  /** False while the Backlog's own read is in flight, so the team points it records are counted. */
  enabled?: boolean;
  /** The Backlog read's sprint exceptions: This sprint counts these, so it matches the sprint items view. */
  sprintEntries?: number;
  onClose: () => void;
  /** The plain text Copy summary puts on the clipboard for the metrics on screen (frame E3). */
  summaryFor: (metrics: GroomingMetrics) => string;
  /** "See the … ›": the items behind a line (frame E2). */
  onOpenItems: (kind: ProgressItemsKind) => void;
}

/** The panel itself (frames E1 and E4). */
export function GroomingProgressPanel({
  project,
  sinceDays,
  onSinceDaysChange,
  variant,
  enabled = true,
  sprintEntries,
  onClose,
  summaryFor,
  onOpenItems,
}: GroomingProgressPanelProps) {
  const metrics = useGroomingMetrics(project, sinceDays, { enabled });
  const data =
    metrics.data && sprintEntries !== undefined ? withBacklogSprint(metrics.data, sprintEntries) : metrics.data;
  const copy = useCopySummary();
  return (
    <aside className={PANEL_CLASSES[variant]} data-testid="grooming-progress-panel" data-variant={variant} aria-label="How grooming is going">
      <div className="sticky top-0 flex max-h-[calc(100vh-2rem)] flex-col overflow-y-auto">
        <div className="flex items-center gap-2 px-4 pb-2.5 pt-3.5">
          <h2 className="min-w-0 flex-1 text-base font-semibold text-foreground">How grooming is going</h2>
          <button
            type="button"
            className="inline-flex h-[26px] items-center rounded-lg px-1.5 text-muted-foreground hover:bg-[#141417] hover:text-foreground"
            aria-label="Close How grooming is going"
            title="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-[#1F1F23] px-4 pb-3">
          <WindowSwitch
            value={sinceDays}
            onChange={(days) => {
              // New figures: what was copied for the old window no longer matches them.
              copy.dismiss();
              onSinceDaysChange(days);
            }}
          />
          <span className="text-xs text-muted-foreground" data-testid="progress-window-dates">
            {windowDates(sinceDays)}
          </span>
        </div>

        {copy.outcome ? (
          <CopiedSummary outcome={copy.outcome} onBack={copy.dismiss} />
        ) : data ? (
          <div className="flex flex-col" data-testid="progress-lines">
            <SizingBlock
              metrics={data}
              onOpen={() => {
                onOpenItems("sizing");
              }}
            />
            <PreparationBlock
              metrics={data}
              sinceDays={sinceDays}
              onOpen={() => {
                onOpenItems("preparation");
              }}
            />
            <SprintBlock
              metrics={data}
              onOpen={() => {
                onOpenItems("sprint");
              }}
            />
          </div>
        ) : metrics.isError ? (
          <div className="flex flex-col items-start gap-2.5 px-4 py-4" data-testid="progress-error">
            <p className={TOOLBOX_TEXT.content}>How grooming is going could not be read.</p>
            <button
              type="button"
              className={SECONDARY_BUTTON}
              disabled={metrics.isFetching}
              onClick={() => {
                void metrics.refetch();
              }}
            >
              {metrics.isFetching ? "Trying again…" : "Try again"}
            </button>
          </div>
        ) : (
          <div className="flex min-h-[160px] items-center justify-center" role="status" aria-live="polite">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="sr-only">Reading how grooming is going…</span>
          </div>
        )}

        <div className="mt-auto flex flex-col gap-2.5 border-t border-[#1F1F23] px-4 pb-3.5 pt-3">
          <p className="text-xs text-muted-foreground">{PROGRESS_FOOTNOTE}</p>
          <div className="flex items-center gap-3">
            <CopySummaryButton
              justCopied={copy.justCopied}
              disabled={!data}
              onClick={() => {
                if (data) void copy.copy(summaryFor(data));
              }}
            />
            <span className={cn("ml-auto text-xs", TOOLBOX_TEXT.faint)}>{PROGRESS_AUDIENCE}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}
